import { useState } from 'react';
import {
  useAdminUpdateOrgTierMutation,
  OrganizationTier,
  TrialDurationWeeks,
  AdminSearchOrganizationQuery,
} from 'src/generated/graphql';
import { formatDistanceToNow } from 'date-fns';

type Organization = NonNullable<AdminSearchOrganizationQuery['organization']>;

type TierChangeSectionProps = {
  organization: Organization;
  onTierChange: () => void;
};

const TIER_OPTIONS: { value: OrganizationTier; label: string; description: string }[] = [
  { value: OrganizationTier.Free, label: 'Free', description: 'Standard free tier' },
  { value: OrganizationTier.Pro, label: 'Pro', description: 'Paid professional tier' },
  { value: OrganizationTier.ProTrial, label: 'Pro Trial', description: 'Time-limited Pro access' },
  { value: OrganizationTier.LegacyFree, label: 'Legacy Free', description: 'Grandfathered free tier' },
];

const TRIAL_DURATION_OPTIONS = [
  { value: TrialDurationWeeks.TwoWeeks, label: '2 weeks' },
  { value: TrialDurationWeeks.FourWeeks, label: '4 weeks' },
  { value: TrialDurationWeeks.SixWeeks, label: '6 weeks' },
  { value: TrialDurationWeeks.EightWeeks, label: '8 weeks' },
];

export const TierChangeSection = ({
  organization,
  onTierChange,
}: TierChangeSectionProps) => {
  const [selectedTier, setSelectedTier] = useState<OrganizationTier>(
    organization.tier as OrganizationTier
  );
  const [trialDuration, setTrialDuration] = useState<TrialDurationWeeks>(
    TrialDurationWeeks.FourWeeks
  );
  const [showConfirm, setShowConfirm] = useState(false);

  const [updateTier, { loading, error }] = useAdminUpdateOrgTierMutation({
    onCompleted: () => {
      setShowConfirm(false);
      onTierChange();
    },
  });

  const currentTier = organization.tier as OrganizationTier;
  const hasChanged = selectedTier !== currentTier;
  const isProTrialSelected = selectedTier === OrganizationTier.ProTrial;
  const isCurrentlyExpiredTrial =
    currentTier === OrganizationTier.ProTrial && organization.isTrialExpired;

  const handleConfirm = () => {
    updateTier({
      variables: {
        organizationSlug: organization.slug,
        tier: selectedTier,
        trialDurationWeeks: isProTrialSelected ? trialDuration : undefined,
      },
    });
  };

  return (
    <div className="border-t border-gray-200 pt-4 mt-4">
      <h4 className="text-sm font-medium text-gray-700 mb-3">
        Organization Tier
      </h4>

      {/* Expired trial indicator */}
      {isCurrentlyExpiredTrial && (
        <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          <span className="font-semibold">Trial Expired</span> - This organization's Pro Trial has expired.
          Effective tier is now <span className="font-semibold">FREE</span>.
        </div>
      )}

      {/* Tier audit info */}
      {organization.tierUpdatedAt && (
        <div className="mb-3 text-xs text-gray-500">
          Last updated{' '}
          {formatDistanceToNow(new Date(organization.tierUpdatedAt), {
            addSuffix: true,
          })}
          {organization.tierUpdatedBy?.email && (
            <> by <span className="font-mono">{organization.tierUpdatedBy.email}</span></>
          )}
        </div>
      )}

      {/* Tier selector */}
      <div className="flex items-center gap-3 mb-3">
        <select
          value={selectedTier}
          onChange={(e) => setSelectedTier(e.target.value as OrganizationTier)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
        >
          {TIER_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Trial duration selector (shown when PRO_TRIAL is selected) */}
        {isProTrialSelected && (
          <select
            value={trialDuration}
            onChange={(e) => setTrialDuration(e.target.value as TrialDurationWeeks)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
          >
            {TRIAL_DURATION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}

        {hasChanged && !showConfirm && (
          <button
            onClick={() => setShowConfirm(true)}
            className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 font-medium text-sm"
          >
            Change Tier
          </button>
        )}
      </div>

      {/* Confirmation dialog */}
      {showConfirm && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-md space-y-3">
          <div className="text-sm text-amber-800">
            Are you sure you want to change the tier from{' '}
            <span className="font-semibold">{currentTier}</span> to{' '}
            <span className="font-semibold">{selectedTier}</span>?
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleConfirm}
              disabled={loading}
              className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 font-medium text-sm disabled:opacity-50"
            >
              {loading ? 'Updating...' : 'Confirm Change'}
            </button>
            <button
              onClick={() => {
                setShowConfirm(false);
                setSelectedTier(currentTier);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          Error: {error.message}
        </div>
      )}
    </div>
  );
};
